import { FormTemplatesByIdsFilters, FormTemplatesResponse } from "../models/forms";
import { ProjectId, UUIDv4 } from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

export const DEFAULT_FORM_TEMPLATES_BY_IDS_FILTERS = {
  limit: 1000,
  skip: 0,
  status: "active" as const,
  include_components: true,
};

/**
 * Creates a form-templates-service client from an already-configured
 * `AssetsHttpClient` instance pointed at the form-templates backend origin.
 *
 * Design note — non-standard base path:
 * The form-templates service does not follow the `Endpoints`/
 * `getAssetsServiceBasePath` v1/v2/v3 convention — it hardcodes a
 * `legacy/v1/projects` path segment with its own backend origin. Construct a
 * second `AssetsHttpClient` instance pointed at that origin and use
 * `http.baseUrl` directly (see `getHqClient` for the same pattern).
 *
 * @example
 * ```ts
 * const formTemplatesHttp = createAssetsHttpClient({
 *   fetch: (url, init) => fetchWithToken(url, { ...init, headers: { region: getRegion(), ...init?.headers } }),
 *   baseUrl: getEnvValue("FORM_TEMPLATES_BACKEND"),
 * });
 * const client = getFormTemplateClient(formTemplatesHttp);
 * ```
 */
export const getFormTemplateClient = (http: AssetsHttpClient) => ({
  getFormTemplatesByIds: async ({
    ids,
    projectId,
    filters,
  }: {
    ids: UUIDv4[];
    projectId: ProjectId;
    filters?: FormTemplatesByIdsFilters;
  }): Promise<FormTemplatesResponse> => {
    const searchString = buildSearchString({
      uids: ids,
      ...DEFAULT_FORM_TEMPLATES_BY_IDS_FILTERS,
      ...filters,
    });

    return http.get<FormTemplatesResponse>(
      `${http.baseUrl}/legacy/v1/projects/${projectId}/${Endpoints.FormTemplates}${
        searchString ? `?${searchString}` : ""
      }`
    );
  },
});

/**
 * The shape returned by {@link getFormTemplateClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getFormTemplateClient>`.
 */
export type FormTemplateClient = ReturnType<typeof getFormTemplateClient>;
